import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useContext,
} from 'react';

// 翻译函数类型
export type TranslateFunc = (key: string) => string;

// 翻译方法集合
export type Translates = Record<string, TranslateFunc>;

// 处理函数类型：接收 cond 数据，返回翻译方法集合
export type TranslateProcessor = (cond: any) => Translates;

// Context 类型（内部使用）
export interface ITranslateContext {
  translates: Translates; // 翻译方法集合
  setConfig: (data: any) => void; // 设置配置数据
}

// Hook 返回类型（展开后的类型，使用索引签名避免冲突）
export type UseTranslateConfigReturn = {
  [key: string]: TranslateFunc | ((data: any) => void);
  setConfig: (data: any) => void;
};

const TranslateConfigContext = createContext<ITranslateContext | undefined>(
  undefined,
);

// children 类型：普通 ReactNode
type Children = React.ReactNode;

interface ITranslateConfigProviderProps {
  children: Children;
  processor: TranslateProcessor; // 处理函数
}

// Provider 组件
export function TranslateConfigProvider({
  children,
  processor,
}: ITranslateConfigProviderProps) {
  const [translates, setTranslates] = useState<Translates>({});

  // 使用 useCallback 缓存 setConfig，避免每次渲染创建新函数
  const setConfig = useCallback(
    (data: any) => {
      const result = processor(data);
      setTranslates(result);
    },
    [processor],
  );

  // 使用 useMemo 缓存 value，只有 translates 变化时才创建新对象
  const value = useMemo(
    () => ({ translates, setConfig }),
    [translates, setConfig],
  );

  return (
    <TranslateConfigContext.Provider value={value}>
      {children}
    </TranslateConfigContext.Provider>
  );
}

// 自定义 Hook：获取翻译配置（可选模式，没有 Provider 时返回 undefined）
// 返回展开后的类型，直接访问 translateType 等方法
export function useTranslateConfig(): UseTranslateConfigReturn {
  const context = useContext(TranslateConfigContext);
  if (!context) return {} as any;
  // 展开 translates，使访问更扁平
  return { ...context.translates, setConfig: context.setConfig };
}
